// Command that wraps a pixel mode conversion for undo/redo.
// Captures the original pixel data of every affected raster layer on first
// apply and restores it (plus the document's previous colorMode) on invert.
//
// audit i131: Image > Mode conversions in Photoshop apply to the WHOLE
// document, not just the active layer. ModeCommand now accepts either a
// single layer id (legacy, unchanged behavior) or a list of raster layer ids
// -- use `ModeCommand.forDocument()` to build the "every raster layer in the
// doc, including ones nested in groups" list automatically -- plus an
// optional `colorMode` to stamp on `doc.colorMode` once every layer has been
// converted (and restore on undo).

import type { Document, DocumentColorMode } from "../document/Document";
import type { Layer, RasterLayer } from "../document/Layer";
import type { PixelBuffer } from "../pixel/PixelBuffer";
import type { Command } from "./Command";

export type ModeType =
  | "grayscale"
  | "indexed"
  | "lab"
  | "cmyk"
  | "16bit"
  | "8bit";

interface LayerSnapshot {
  readonly data: Uint8ClampedArray;
  readonly width: number;
  readonly height: number;
}

export class ModeCommand implements Command {
  readonly kind = "mode-change";

  private readonly layerIds: readonly string[];
  private readonly colorMode: DocumentColorMode | undefined;

  private snapshots: Map<string, LayerSnapshot> | null = null;
  private prevColorMode: DocumentColorMode | undefined;
  private capturedColorMode = false;

  /**
   * @param layerId Either a single raster layer id (legacy single-layer
   *   conversion -- existing call sites keep working unchanged) or a list of
   *   raster layer ids to convert together as one undoable step.
   * @param mode Mode-change label (used for the `kind`/history display).
   * @param converter Applied in place to each affected layer's PixelBuffer.
   * @param colorMode When provided, also stamps `doc.colorMode` after every
   *   listed layer has been converted, and restores the previous value on
   *   undo. Optional so existing single-layer call sites that never touched
   *   the document-level mode keep compiling unchanged.
   */
  constructor(
    layerId: string | readonly string[],
    private readonly mode: ModeType,
    private readonly converter: (buffer: PixelBuffer) => void,
    colorMode?: DocumentColorMode,
  ) {
    this.layerIds = typeof layerId === "string" ? [layerId] : layerId;
    this.colorMode = colorMode;
  }

  /**
   * Build a ModeCommand that converts EVERY raster layer in `doc` (including
   * ones nested inside groups) as one undo step, and sets `doc.colorMode` to
   * `colorMode` once applied.
   */
  static forDocument(
    doc: Document,
    mode: ModeType,
    converter: (buffer: PixelBuffer) => void,
    colorMode?: DocumentColorMode,
  ): ModeCommand {
    return new ModeCommand(collectRasterLayerIds(doc.layers), mode, converter, colorMode);
  }

  apply(doc: Document): Document {
    if (this.snapshots === null) {
      this.snapshots = new Map();
      for (const id of this.layerIds) {
        const layer = findRaster(doc.layers, id);
        if (!layer) continue;
        this.snapshots.set(id, {
          data: new Uint8ClampedArray(layer.pixels.data),
          width: layer.pixels.width,
          height: layer.pixels.height,
        });
      }
    }
    if (!this.capturedColorMode) {
      this.prevColorMode = doc.colorMode;
      this.capturedColorMode = true;
    }

    for (const id of this.layerIds) {
      const layer = findRaster(doc.layers, id);
      if (!layer) continue;
      this.converter(layer.pixels);
    }

    if (this.colorMode !== undefined) {
      doc.colorMode = this.colorMode;
    }

    return doc;
  }

  invert(_before: Document, _after: Document): Command {
    const snaps = this.snapshots;
    if (snaps === null || snaps.size === 0) {
      return new NoOpModeCommand();
    }
    return new RestorePixelsModeCommand(
      snaps,
      this.prevColorMode,
      this.colorMode !== undefined,
    );
  }
}

class RestorePixelsModeCommand implements Command {
  readonly kind = "mode-change/restore";

  constructor(
    private readonly snapshots: ReadonlyMap<string, LayerSnapshot>,
    private readonly prevColorMode: DocumentColorMode | undefined,
    private readonly restoreColorMode: boolean,
  ) {}

  apply(doc: Document): Document {
    for (const [id, snap] of this.snapshots) {
      const layer = findRaster(doc.layers, id);
      if (!layer) continue;
      if (layer.pixels.data.length !== snap.data.length) continue;
      layer.pixels.data.set(snap.data);
      layer.pixels.markDirty();
    }
    if (this.restoreColorMode) {
      // exactOptionalPropertyTypes: `colorMode` is optional, not
      // `T | undefined` -- use `delete` rather than assigning `undefined`
      // when there was no previous mode (i.e. it reverts to the default).
      if (this.prevColorMode !== undefined) {
        doc.colorMode = this.prevColorMode;
      } else {
        delete doc.colorMode;
      }
    }
    return doc;
  }

  invert(before: Document, _after: Document): Command {
    const redoSnapshots = new Map<string, LayerSnapshot>();
    for (const id of this.snapshots.keys()) {
      const layer = findRaster(before.layers, id);
      if (!layer) continue;
      redoSnapshots.set(id, {
        data: new Uint8ClampedArray(layer.pixels.data),
        width: layer.pixels.width,
        height: layer.pixels.height,
      });
    }
    return new RestorePixelsModeCommand(redoSnapshots, before.colorMode, this.restoreColorMode);
  }
}

class NoOpModeCommand implements Command {
  readonly kind = "mode-change/noop";
  apply(doc: Document): Document {
    return doc;
  }
  invert(): Command {
    return new NoOpModeCommand();
  }
}

function findRaster(layers: readonly Layer[], id: string): RasterLayer | null {
  for (const l of layers) {
    if (l.id === id && l.kind === "raster") return l;
    if (l.kind === "group") {
      const inner = findRaster(l.children, id);
      if (inner) return inner;
    }
  }
  return null;
}

/**
 * Collects the ids of every raster layer in `layers`, including ones nested
 * inside groups. Used by `ModeCommand.forDocument()` to convert the whole
 * document rather than just the active layer (audit i131).
 */
export function collectRasterLayerIds(layers: readonly Layer[]): string[] {
  const ids: string[] = [];
  for (const l of layers) {
    if (l.kind === "raster") ids.push(l.id);
    else if (l.kind === "group") ids.push(...collectRasterLayerIds(l.children));
  }
  return ids;
}
